import { settlementService } from '@/api/settlement.service';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';

const QUERY_KEY = 'settlement';

const BATCH_PAGE_SIZE = 5;
const UNSETTLED_PAGE_SIZE = 20;
const CANDIDATES_PAGE_SIZE = 20;

export const useSettlementCashFlow = (enabled: MaybeRefOrGetter<boolean> = true) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'cash-flow'],
    queryFn: () => settlementService.getCashFlowOverview(),
    enabled: computed(() => toValue(enabled)),
  });
};

export const useSettlementBatches = (enabled: MaybeRefOrGetter<boolean> = true) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY, 'batches'],
    queryFn: ({ pageParam }) =>
      settlementService.getProcessedBatches({
        page: pageParam as number,
        limit: BATCH_PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    enabled: computed(() => toValue(enabled)),
  });
};

export const useSettlementBatch = (
  batchNumber: MaybeRefOrGetter<number | null>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'batch', batchNumber],
    queryFn: () => settlementService.getProcessedBatch(toValue(batchNumber)!),
    enabled: computed(() => toValue(enabled) && toValue(batchNumber) != null),
    retry: false,
  });
};

export const useSettlementUnsettledPayments = (
  search: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: computed(() => [QUERY_KEY, 'unsettled-payments', toValue(search)]),
    queryFn: ({ pageParam }) =>
      settlementService.getUnsettledPayments({
        page: pageParam as number,
        limit: UNSETTLED_PAGE_SIZE,
        q: toValue(search) || undefined,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    enabled: computed(() => toValue(enabled)),
  });
};

export const useSettlementBatchDetails = (
  batchNumber: MaybeRefOrGetter<number | null>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'batch-details', batchNumber],
    queryFn: () => settlementService.getBatchDetails(toValue(batchNumber)!),
    enabled: computed(() => toValue(enabled) && toValue(batchNumber) != null),
  });
};

export const useSettlementPaymentCandidates = (
  detailId: MaybeRefOrGetter<string | null>,
  search: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: computed(() => [QUERY_KEY, 'payment-candidates', toValue(detailId), toValue(search)]),
    queryFn: ({ pageParam }) =>
      settlementService.getPaymentCandidates({
        detailId: toValue(detailId)!,
        page: pageParam as number,
        limit: CANDIDATES_PAGE_SIZE,
        q: toValue(search) || undefined,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    enabled: computed(() => toValue(enabled) && !!toValue(detailId)),
  });
};

export const useAssignSettlementPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ detailId, paymentId }: { detailId: string; paymentId: string }) =>
      settlementService.assignPayment(detailId, paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};
